import { useVirtualizer } from "@tanstack/react-virtual";
import type React from "react";
import { useRef } from "react";
import { normalSanatizerBRL } from "src/utils/functions";
import type { MonthlySnapshot } from "../types";

interface RentVsBuyDesktopTableProps {
  monthlyData: MonthlySnapshot[];
}

export const RentVsBuyDesktopTable: React.FC<RentVsBuyDesktopTableProps> = ({
  monthlyData,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: monthlyData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 49,
    overscan: 10,
  });

  return (
    <div
      ref={parentRef}
      className="hidden md:block w-full h-[500px] overflow-auto rounded-xl border border-gray-200 shadow-sm relative"
    >
      <div className="grid grid-cols-[70px_repeat(7,1fr)] text-xs uppercase bg-gray-50 text-gray-700 sticky top-0 shadow-sm z-10 w-full min-w-[900px]">
        <div className="px-3 py-4 font-semibold text-center flex items-center justify-center">
          Mês
        </div>
        <div className="px-3 py-4 font-semibold text-center flex items-center justify-center">
          Prestação
        </div>
        <div className="px-3 py-4 font-semibold text-center text-blue-700 bg-blue-50/50 flex items-center justify-center">
          Aluguel
        </div>
        <div className="px-3 py-4 font-semibold text-center text-green-700 bg-green-50/50 flex items-center justify-center">
          Saldo Devedor
        </div>
        <div className="px-3 py-4 font-semibold text-center flex items-center justify-center">
          Aporte Financ.
        </div>
        <div className="px-3 py-4 font-semibold text-center text-yellow-700 bg-yellow-50/50 flex items-center justify-center">
          Aporte Alug.
        </div>
        <div className="px-3 py-4 font-semibold text-center text-green-700 bg-green-50/30 flex items-center justify-center">
          Patrim. Financ.
        </div>
        <div className="px-3 py-4 font-semibold text-center text-yellow-700 bg-yellow-50/30 flex items-center justify-center">
          Patrim. Alug.
        </div>
      </div>

      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
        className="bg-white text-sm min-w-[900px]"
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const snapshot = monthlyData[virtualItem.index];

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={rowVirtualizer.measureElement}
              className="absolute top-0 left-0 w-full grid grid-cols-[70px_repeat(7,1fr)] hover:bg-gray-50/50 transition-colors border-b border-gray-100"
              style={{
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <div className="px-3 py-3 text-center font-medium text-gray-900 flex items-center justify-center">
                {snapshot.month}
              </div>
              <div className="px-3 py-3 text-center flex items-center justify-center">
                <span className="text-gray-400 mr-1">R$</span>
                {normalSanatizerBRL(snapshot.installment)}
              </div>
              <div className="px-3 py-3 text-center bg-blue-50/20 text-blue-900 font-medium flex items-center justify-center">
                <span className="text-blue-400 mr-1">R$</span>
                {normalSanatizerBRL(snapshot.rent)}
              </div>
              <div className="px-3 py-3 text-center bg-green-50/20 text-green-900 font-medium flex items-center justify-center">
                <span className="text-green-400 mr-1">R$</span>
                {normalSanatizerBRL(snapshot.outstandingBalance)}
              </div>
              <div className="px-3 py-3 text-center text-gray-600 flex items-center justify-center">
                <span className="text-gray-400 mr-1">R$</span>
                {normalSanatizerBRL(snapshot.investmentFinancing)}
              </div>
              <div className="px-3 py-3 text-center bg-yellow-50/20 text-yellow-900 font-medium flex items-center justify-center">
                <span className="text-yellow-400 mr-1">R$</span>
                {normalSanatizerBRL(snapshot.investmentRenting)}
              </div>
              <div className="px-3 py-3 text-center bg-green-50/10 text-green-800 font-medium flex items-center justify-center">
                <span className="text-green-400 mr-1">R$</span>
                {normalSanatizerBRL(snapshot.accumulatedWealthFinancing)}
              </div>
              <div className="px-3 py-3 text-center bg-yellow-50/10 text-yellow-800 font-medium flex items-center justify-center">
                <span className="text-yellow-400 mr-1">R$</span>
                {normalSanatizerBRL(snapshot.accumulatedWealthRenting)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
